import {useCallback, useMemo} from 'react';

import {
  createCommentCallbackFnType,
  INLINE_MENTION_REGEXP,
  MentionableUser,
  PANEL_MENTION_REGEXP,
  USER_MENTION_REGEXP,
} from '../../components/ReportDiscussion/types';
import {InlineCommentDetail} from '../../components/Slate/plugins/inline-comments-common';
import {
  CreateViewDiscussionCommentMutationVariables,
  useCreateViewDiscussionCommentMutation,
  useDeleteDiscussionCommentMutation,
  useTeamMembersQuery,
  useViewDiscussionThreadsLazyQuery,
} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {useIsGalleryReport} from '../../util/gallery';
import {usePropsSelector, useSelector} from '../hooks';
import {useViewer} from '../viewer/hooks';
import * as ViewApi from '../views/api';
import {Ref as DiscussionCommentRef} from '../views/discussionComment/types';
import {Ref as DiscussionThreadRef} from '../views/discussionThread/types';
import * as ViewHooks from '../views/hooks';
import * as ReportActions from '../views/report/actions';
import * as ViewSelectors from '../views/selectors';
import * as ReportViewsSelectors from './selectors';
import {ReportViewRef} from './types';

export function useReportDiscussion(props: {
  reportServerID: string;
  reportViewRef: ReportViewRef;
}) {
  const viewer = useViewer();
  const view = useSelector(state => state.views.views[props.reportViewRef.id]);

  const readOnly = view.parentId == null;
  const reportServerID = view.parentId ?? props.reportServerID;

  const reportRef = useSelector(
    ReportViewsSelectors.getReportRef(props.reportViewRef)
  );

  /* Load discussion threads */
  const loadDiscussionThreads = ViewHooks.useViewAction(
    reportRef,
    ReportActions.loadDiscussionThreads
  );

  const [fetchDiscussionThreads, discussionThreadsQuery] =
    useViewDiscussionThreadsLazyQuery({
      variables: {
        viewID: reportServerID,
      },
      fetchPolicy: 'no-cache',
      pollInterval: 45000,
      context: propagateErrorsContext(),
      onCompleted(data) {
        loadDiscussionThreads({
          discussionThreads: ViewApi.parseDiscussionThreads(data),
        });
      },
    });

  const {isGalleryReport} = useIsGalleryReport(reportServerID);

  /* Create comment */
  const [createDiscussionCommentMutation, {loading: loadingCreateComment}] =
    useCreateViewDiscussionCommentMutation();
  const addDiscussionComment = ViewHooks.useViewAction(
    reportRef,
    ReportActions.addDiscussionComment
  );
  const createComment = useCallback(
    (
      mutationVars: Omit<
        CreateViewDiscussionCommentMutationVariables,
        'viewID' | 'notifyAllSubscribers'
      >,
      discussionThreadRef?: DiscussionThreadRef,
      commentDetails?: InlineCommentDetail[],
      callbackFn?: createCommentCallbackFnType
    ) => {
      const inlineCommentDetails =
        commentDetails != null && commentDetails.length > 0
          ? JSON.stringify(commentDetails)
          : undefined;

      createDiscussionCommentMutation({
        variables: {
          ...mutationVars,
          viewID: reportServerID,
          notifyAllSubscribers: !isGalleryReport,
          inlineCommentDetails,
        },
      }).then(({data}) => {
        if (data?.createViewDiscussionComment == null) {
          throw new Error('Unexpected response for createDiscussionComment');
        }

        const mentionTypes = [];
        if (USER_MENTION_REGEXP.test(mutationVars.body)) {
          mentionTypes.push('user');
        }
        if (INLINE_MENTION_REGEXP.test(mutationVars.body)) {
          mentionTypes.push('inline text');
        }
        if (PANEL_MENTION_REGEXP.test(mutationVars.body)) {
          mentionTypes.push('panel');
        }

        const {discussionThread, discussionComment, alertSubscription} =
          data.createViewDiscussionComment;

        window.analytics?.track('Comment Created', {
          mode: readOnly ? 'view' : 'edit',
          reportID: reportServerID,
          commentID: discussionComment.id,
          mentionTypes,
        });

        callbackFn?.(commentDetails, discussionThread.id, discussionComment.id);
        addDiscussionComment(
          {
            // Discussion thread containing the new comment (may or may not be newly-created)
            discussionThread: {
              ...discussionThread,
              poster: ViewApi.parseUser(discussionThread.poster),
              comments: [], // no need to fetch all comments, just need the new one
            },
            // The new comment
            discussionComment: {
              ...discussionComment,
              poster: ViewApi.parseUser(discussionComment.poster),
              updatedAt: discussionComment.updatedAt ?? undefined,
            },
            // The AlertSubscription for the poster
            alertSubscription,
          },
          discussionThreadRef
        );
      });
    },
    [
      readOnly,
      addDiscussionComment,
      createDiscussionCommentMutation,
      reportServerID,
      isGalleryReport,
    ]
  );

  /* Delete comment */
  const [deleteDiscussionCommentMutation] =
    useDeleteDiscussionCommentMutation();
  const deleteDiscussionCommentAction = ViewHooks.useViewAction(
    reportRef,
    ReportActions.deleteDiscussionComment
  );
  const deleteDiscussionThreadAction = ViewHooks.useViewAction(
    reportRef,
    ReportActions.deleteDiscussionThread
  );

  const deleteComment = useCallback(
    (params: {
      discussionCommentServerID: string;
      discussionCommentRef: DiscussionCommentRef;
      discussionThreadRef: DiscussionThreadRef;
      deleteThread: boolean;
    }) => {
      const {
        discussionCommentServerID,
        discussionCommentRef,
        discussionThreadRef,
        deleteThread,
      } = params;

      deleteDiscussionCommentMutation({
        variables: {id: discussionCommentServerID, deleteThread},
      }).then(({data}) => {
        if (data?.deleteDiscussionComment?.success) {
          // If we delete the first comment in a thread, that's equivalent to deleting the thread
          if (deleteThread) {
            deleteDiscussionThreadAction({
              discussionThreadRef,
            });
          } else {
            deleteDiscussionCommentAction({
              discussionCommentRef,
              discussionThreadRef,
              deleteThread,
            });
          }
        } else {
          throw new Error(
            `Delete comment failed (id: ${discussionCommentServerID}`
          );
        }
      });
    },
    [
      deleteDiscussionCommentAction,
      deleteDiscussionCommentMutation,
      deleteDiscussionThreadAction,
    ]
  );

  const discussionThreadRefs = useSelector(
    ReportViewsSelectors.getReportDiscussionThreadRefs(props.reportViewRef)
  );

  const entityName = useSelector(
    ReportViewsSelectors.getReportEntityName(props.reportViewRef)
  );

  const teamMembersQuery = useTeamMembersQuery({
    variables: {entityName},
  });

  const teamMembers: MentionableUser[] = useMemo(() => {
    if (
      !teamMembersQuery.loading &&
      teamMembersQuery.data?.entity?.members != null
    ) {
      return teamMembersQuery.data.entity.members
        .filter(m => m.username != null && m.username !== viewer?.username)
        .map(
          m =>
            ({
              id: m.id,
              username: m.username,
              name: m.name,
              photoUrl: m.photoUrl,
            }) as MentionableUser
        );
    } else {
      return [];
    }
  }, [teamMembersQuery.data, teamMembersQuery.loading, viewer]);

  const getReportPanels = useMemo(
    () => ReportViewsSelectors.makeReportPanelsSelector(props.reportViewRef),
    [props.reportViewRef]
  );
  const reportPanels = getReportPanels();

  // Panel comments are disabled in old reports until the author saves them again, because the panels don't have persistent IDs
  // See ActionsInternal.loadFinished in reducer.ts for more info
  const panelCommentsEnabled = usePropsSelector(
    ViewSelectors.makePanelCommentsEnabledSelector,
    props.reportViewRef
  );

  const getCommentsCount = useMemo(
    () =>
      ReportViewsSelectors.getReportDiscussionCommentsCount(
        props.reportViewRef
      ),
    [props.reportViewRef]
  );
  const commentsCount = getCommentsCount();

  return useMemo(
    () => ({
      fetchDiscussionThreads,
      loadingDiscussionThreads: discussionThreadsQuery.loading,
      commentsCount,
      discussionThreadRefs,
      createComment,
      loadingCreateComment,
      deleteComment,
      viewer,
      readOnly,
      teamMembers,
      reportPanels,
      panelCommentsEnabled,
    }),
    [
      createComment,
      deleteComment,
      discussionThreadRefs,
      discussionThreadsQuery.loading,
      fetchDiscussionThreads,
      loadingCreateComment,
      panelCommentsEnabled,
      reportPanels,
      teamMembers,
      viewer,
      readOnly,
      commentsCount,
    ]
  );
}
